import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import { useTheme } from "@material-ui/core/styles";
import { useSetState } from "Helpers/Hooks";
import DialogContent from "@material-ui/core/DialogContent";
import Input from "Components/Input/Input";
import Grid from "@material-ui/core/Grid";
import Translator from "Languages/Utility/Translator";
import { read } from "Helpers/LocalStorage";
import showNotify from "Helpers/Notify";
import MuiButton from "Components/MuiButton/MuiButton";
import { DialogActions, FormControlLabel, Switch } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import * as Icons from "react-bootstrap-icons";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";
import AccountService from "Services/http/endpoints/superadmin/AccountsEndpoints";
import StatusHandler from "Helpers/StatusHandler";
import * as R from "ramda";
import { useDispatch, useSelector } from "react-redux";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import { getAccountingDiscountCardAgain } from "Stores/actions";
import DatePickerComponent from "Components/DatePickerComponent/DatePickerComponent";
import { toggleNPercentRiyal } from "Constants/Constants";
import moment from "moment-jalaali";

export default function AddDiscountCard({ disabled }) {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formValid, setFormValid] = useState({});
  const [loadData, setLoadData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkValid, setCheckValid] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const { activePeriods } = useSelector((state) => ({
    activePeriods: state.saAccounts.activePeriods,
    allGroups: state.saAccounts.allGroups,
    discountCardStype: state.saAccounts.discountCardStype,
    discountCardRefrence: state.saAccounts.discountCardRefrence,
  }));
  const [controls, setControls] = useSetState({
    fromDate: {
      type: "date",
      value: "",
      validation: null,
    },
    toDate: {
      type: "date",
      value: "",
      validation: null,
    },
    periods: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
    stype: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
    refrence: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
    group: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
    title: {
      type: "text",
      value: "",
      validation: {
        required: true,
      },
    },
    discountCode: {
      type: "text",
      value: "",
      validation: {
        required: true,
      },
    },
    minDiscountUsage: {
      type: "price",
      value: "",
      validation: {
        isPrice: true,
      },
    },
    usageCount: {
      type: "number",
      value: "",
      validation: {
        required: true,
        isNumeric: true,
        notZero: true,
      },
    },
    value: {
      type: "price",
      value: "",
      validation: {
        required: true,
        isPrice: true,
        notZero: true,
      },
      relation: "isPercent",
    },
    isPercent: {
      type: "toggle",
      relation: "minDiscountUsage",
      arrayItems: toggleNPercentRiyal,
      value: toggleNPercentRiyal[0],
      ValidationPlus: {
        isPercent: true,
      },
    },
    options_active: {
      type: "switch",
      items: [{ value: false, key: "isActive" }],
    },
  });

  const onShowDialog = () => {
    setShowDialog(true);
  };

  const onEnter = async () => {
    setLoading(true);
    // reset variables
    setControls({ title: { value: "" } });
    setControls({ discountCode: { value: "" } });
    setControls({ minDiscountUsage: { value: 0 } });
    setControls({ usageCount: { value: "" } });
    setControls({ value: { value: "" } });
    setControls({ fromDate: { value: moment() } });
    setControls({ toDate: { value: moment().endOf("jMonth") } });
    setControls({ isPercent: { value: toggleNPercentRiyal[0] } });
    controls.options_active.items.map((items) => (items.value = false));
    setFormValid({
      title: false,
      discountCode: false,
      usageCount: false,
      value: false,
    });
    // get other data
    await getAllOtherData();
    // fill selects
    if (activePeriods) {
      const _periods = activePeriods.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({
        periods: {
          selectItems: _periods,
          value: _periods[0],
        },
      });
    } else {
      showNotify({ text: t("getDataError"), customClass: "error" });
      onExit();
    }
    setLoadData(true);
    setLoading(false);
  };

  const getAllOtherData = async () => {
    if (showDialog) {
      setLoading(true);
      return new Promise((resolve) => {
        (async () => {
          try {
            const params = {
              KeyGroup: read("groupAKey"),
              KeyUser: read("userAKey"),
            };
            const resAllGroup =
              await AccountService.getAccountsAllGroup(params);
            let err = StatusHandler(resAllGroup);
            if (!err.isFail) {
              const selectItems = resAllGroup.data.Value.map((child) => ({
                label: child.Name,
                value: child.Id,
              }));
              setControls({ group: { selectItems, value: selectItems[0] } });
            }
            const resStypes =
              await AccountService.getAccountingDiscountCardStype(params);
            err = StatusHandler(resStypes);
            if (!err.isFail) {
              const selectItems = resStypes.data.Value.map((child) => ({
                label: child.Name,
                value: child.Id,
              }));
              setControls({ stype: { selectItems, value: selectItems[0] } });
            }
            setLoading(false);
            resolve(true);
          } catch (err) {
            console.error("Promise error:", err);
            setLoading(false);
            resolve(false);
          }
        })();
      });
    }
  };

  useEffect(() => {
    if (loadData && showDialog) {
      setLoading(true);
      (async () => {
        if (controls.stype.value) {
          const params = {
            KeyGroup: read("groupAKey"),
            KeyUser: read("userAKey"),
            Id: controls.stype.value.value,
          };
          const resRefrences =
            await AccountService.getAccountingDiscountCardRefrence(params);
          let err = StatusHandler(resRefrences);
          if (!err.isFail) {
            const selectItems = resRefrences.data.Value.map((child) => {
              return {
                label: child.Name,
                value: child.Id,
              };
            });
            setControls({
              refrence: {
                selectItems,
                value: selectItems[0],
              },
            });
          }
        }
      })();
      setLoading(false);
    }
  }, [loadData, showDialog, controls.stype.value]);

  const setValue = (name, value, validation) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
  };

  const autoCompleteHandler = (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
  };

  const handleDateChange = (name, value) => {
    setControls({ [name]: { value: value } });
  };

  const switchChange = async (e) => {
    const items = controls.options_active.items;
    items[0].value = e.target.checked;
    setControls({ options_active: { items: items } });
  };

  const onChangeToggle = (name, key, value) => {
    setControls({ [name]: { value: value } });
  };

  const onFinish = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const {
        fromDate,
        toDate,
        periods,
        stype,
        refrence,
        group,
        title,
        discountCode,
        minDiscountUsage,
        usageCount,
        value,
        isPercent,
        options_active,
      } = controls;
      if (
        !R.isEmpty(formValid) &&
        Object.values(formValid).every((item) => item === true)
      ) {
        if (isPercent.value.value && value.value > 100) {
          showNotify({
            text: t("wrongPercentage"),
            customClass: "error",
          });
        } else {
          if (fromDate.value > toDate.value) {
            showNotify({ text: t("checkDate"), customClass: "error" });
          } else {
            const data = {
              KeyGroup: read("groupAKey"),
              KeyUser: read("userAKey"),
              PeriodId: periods.value.value,
              StypeId: stype.value.value,
              RefrenceId: refrence.value.value,
              GroupId: group.value.value,
              Code: discountCode.value,
              Name: title.value,
              From: fromDate.value.locale("fa").format("jYYYY/jMM/jDD"),
              To: toDate.value.locale("fa").format("jYYYY/jMM/jDD"),
              IsPercent: isPercent.value.value,
              Value: +value.value,
              MinBuy: +minDiscountUsage.value,
              UsableCount: +usageCount.value,
              IsActive: options_active.items[0].value,
            };
            const response =
              await AccountService.postAccountingDiscountCard(data);
            let err = StatusHandler(response);
            if (!err.isFail) {
              if (response.data.Value.IsError === true) {
                showNotify({
                  text: response.data.Value.Describ,
                  customClass: "error",
                });
              } else {
                showNotify({
                  text: t("mission_accomplished"),
                  customClass: "success",
                });
                await dispatch(getAccountingDiscountCardAgain());
                onExit();
              }
              setTimeout(() => {
                setLoading(false);
              }, 500);
            } else {
              setLoading(false);
            }
          }
        }
      } else {
        setCheckValid(true);
        setLoading(false);
      }
    } catch (err) {
      console.log("[AddDiscountCard]", err);
      setLoading(false);
    }
  };

  const onGenerateCode = () => {
    const randomstring = Math.random().toString(36).slice(-8);
    setValue("discountCode", randomstring, true);
  };

  const onExit = () => {
    setShowDialog(false);
    setLoading(false);
    setCheckValid(false);
  };

  return (
    <>
      <BackDropLoading loading={loading} />
      <MuiButton
        color="primary"
        title={<Translator id="addDiscountCard" />}
        innerIcon={<Icons.PlusLg />}
        onClick={onShowDialog}
        disabled={disabled || !read("FormDiscount").accessRegister}
        hidden={!read("FormDiscount").accessRegister}
      />
      <Dialog
        dir={direction}
        open={showDialog}
        onClose={onExit}
        TransitionProps={{ onEnter: onEnter, onExit: onExit }}
        fullWidth
        maxWidth="md"
      >
        <div className="dialogHeader havebtn">
          <p>
            <Translator id="addDiscountCard" />
          </p>
          <div className="d-flex">
            <FormControlLabel
              className="flex-row-reverse m-0"
              control={
                <Switch
                  className={`switcher ${direction}`}
                  color="primary"
                  checked={controls.options_active.items[0].value}
                  onClick={switchChange}
                />
              }
              label={t(
                controls.options_active.items[0].value ? "active" : "disable",
              )}
            />
          </div>
        </div>
        <DialogContent>
          <Grid container spacing={2}>
            {Object.keys(controls)
              .filter(
                (key) =>
                  controls[key].type !== "switch" &&
                  controls[key].type !== "checkbox",
              )
              .map((key) => {
                return (
                  <Grid item md={4} sm={6} xs={12} key={key}>
                    {controls[key].type === "select" && (
                      <MuiAutoComplete
                        autoSelect={true}
                        options={controls[key].selectItems}
                        label={<Translator id={key} />}
                        value={controls[key].value}
                        onChange={autoCompleteHandler}
                        id={key}
                      />
                    )}
                    {(controls[key].type === "text" ||
                      controls[key].type === "price" ||
                      controls[key].type === "number") && (
                      <Input
                        name={key}
                        setValue={setValue.bind(this)}
                        validation={
                          controls[key].relation &&
                          controls[controls[key].relation].value.value
                            ? {
                                ...controls[key].validation,
                                ...controls[controls[key].relation]
                                  .ValidationPlus,
                              }
                            : controls[key].validation
                        }
                        placeholder={t(key)}
                        label={<Translator id={key} />}
                        type={controls[key].type}
                        checkValid={checkValid}
                        value={controls[key].value}
                        endIcon={
                          key === "discountCode" ? (
                            <Icons.PlusSquare
                              size={22}
                              color="#008ecb"
                              onClick={onGenerateCode}
                              className="handC"
                            />
                          ) : controls[key].type === "price" ? (
                            controls[key].relation ? (
                              controls[controls[key].relation].value.value ? (
                                "%"
                              ) : (
                                <Translator id="moneyUnit" />
                              )
                            ) : (
                              <Translator id="moneyUnit" />
                            )
                          ) : (
                            ""
                          )
                        }
                        radioGroup={
                          controls[key].type === "price" &&
                          controls[key].relation
                            ? {
                                name: controls[key].relation,
                                value: controls[controls[key].relation].value,
                                items:
                                  controls[controls[key].relation].arrayItems,
                                onChange: onChangeToggle,
                              }
                            : null
                        }
                      />
                    )}
                    {controls[key].type === "date" && (
                      <DatePickerComponent
                        label={t(key)}
                        name={key}
                        value={controls[key].value}
                        onChange={(e) => handleDateChange(key, e)}
                      />
                    )}
                  </Grid>
                );
              })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <MuiButton
            color="primary"
            title={<Translator id="button_save" />}
            onClick={onFinish}
          />
          <MuiButton
            onClick={() => setShowDialog(false)}
            title={<Translator id="enseraf" />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
